// URL-only propagation (ARCH-002 amendment *Closed-vocabulary attribution and
// operator-registered links*, point 10): what the page knows about a visit
// travels in the links it hands the visitor — the registration link and our own
// pages — and nowhere else. Nothing is written to the visitor's device, so a
// visit that leaves the site is forgotten, by construction.

import { isRegistrationSelectableTierId } from '@amberon/crm-tenant-entitlements-domain';
import type { VisitAttribution } from './classify.js';

const VALID_INTERVALS = new Set(['monthly', 'yearly', 'annual']);

/** The registration link for a call to action: the plan it sells, and where the visitor came from. */
export function registrationHref(
  appUrl: string,
  attribution: VisitAttribution,
  tier?: string | null,
  interval?: string | null,
): string {
  const query = new URLSearchParams();
  if (isRegistrationSelectableTierId(tier)) query.set('tier', tier);
  query.set('source', 'website');
  if (attribution.ref) query.set('ref', attribution.ref);
  query.set('via', attribution.via);
  if (interval && VALID_INTERVALS.has(interval)) query.set('interval', interval);
  return `${appUrl}${appUrl.includes('?') ? '&' : '?'}${query.toString()}`;
}

/**
 * One of our own paths with the visit appended, so the next page derives the
 * same thing from its URL instead of from a referrer that is us. A link that is
 * not a path of this site — another origin, a mail address, a bare anchor — is
 * returned unchanged: the code identifies a campaign of ours and has no
 * business on someone else's URL.
 */
export function internalHref(href: string, attribution: VisitAttribution): string {
  if (!href.startsWith('/') || href.startsWith('//')) return href;
  if (!attribution.ref && attribution.via === 'direct') return href;

  const [path, hash = ''] = splitHash(href);
  const [base, search = ''] = splitQuery(path);
  const query = new URLSearchParams(search);
  if (attribution.ref) query.set('ref', attribution.ref);
  query.set('via', attribution.via);
  return `${base}?${query.toString()}${hash}`;
}

/** `'/x?a=1#top'` → `['/x?a=1', '#top']`. */
function splitHash(href: string): [string, string?] {
  const index = href.indexOf('#');
  return index === -1 ? [href] : [href.slice(0, index), href.slice(index)];
}

/** `'/x?a=1'` → `['/x', 'a=1']`. */
function splitQuery(path: string): [string, string?] {
  const index = path.indexOf('?');
  return index === -1 ? [path] : [path.slice(0, index), path.slice(index + 1)];
}

/** The path an app link must point at to be a registration call to action. */
const REGISTER_PATH = '/register';

/**
 * Rewrite the page's links in place: our own pages so the visit survives the
 * next click, the registration calls to action so the app learns the plan and
 * the origin. Every other link — another origin, the plain app login — is left
 * exactly as the page author wrote it.
 *
 * App links keep their target in `data-app-url` rather than in `href`
 * (DATA-004: no third-party host in the static source), so the href is built
 * from the data attribute and this runs after the page has hydrated them.
 */
export function decorateLinks(document: Document, attribution: VisitAttribution, interval?: string | null): void {
  document.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((link) => {
    const href = link.getAttribute('href');
    if (href) link.setAttribute('href', internalHref(href, attribution));
  });

  document.querySelectorAll<HTMLAnchorElement>('a[data-app-url]').forEach((link) => {
    const appUrl = link.getAttribute('data-app-url');
    if (!appUrl || !isRegisterUrl(appUrl)) return;
    const tier = link.getAttribute('data-tier');
    link.setAttribute('href', registrationHref(appUrl, attribution, tier, tier ? interval : undefined));
  });
}

/** Update the billing interval in existing registration CTA links with data-tier. */
export function updateRegistrationInterval(document: Document, interval: string): void {
  if (!VALID_INTERVALS.has(interval)) return;
  document.querySelectorAll<HTMLAnchorElement>('a[data-app-url][data-tier]').forEach((link) => {
    const href = link.getAttribute('href');
    if (!href) return;
    try {
      const url = new URL(href);
      url.searchParams.set('interval', interval);
      link.setAttribute('href', url.toString());
    } catch {
      // not a full URL or parse error
    }
  });
}

/** Does this app URL open the registration? */
function isRegisterUrl(appUrl: string): boolean {
  try {
    return new URL(appUrl).pathname.replace(/\/$/, '') === REGISTER_PATH;
  } catch {
    return false;
  }
}
